<template>
  <div id="new-tank-subscription-view">
    <div class="row mb-3 justify-content-center">
      <p class="text-center plan-details-header" data-test-hook="new-plan-with-upgrade-text">
        {{ $t('xFuelMonitorRequiresPremium') }}
      </p>
    </div>
    <b-card
      v-if="selectedProduct"
      class="paid-subs-info-card mb-0 sub-card mx-1"
      body-class="subs-info-card-body"
      data-test-hook="new-tank-subscription-modal-paid-card"
    >
      <template #header>
        <div data-test-hook="new-tank-subscription-header" class="plan-header">
          {{ selectedProduct.name }}
        </div>
      </template>
      <div class="container">
        <b-row>
          <b-col>
            <CadenceSelectionTabs
              :selected-product="selectedProduct"
              :annual-paid-product="annualPaidProduct"
              :monthly-paid-product="monthlyPaidProduct"
              @setSelectedProduct="setSelectedProduct"
            />
          </b-col>
        </b-row>
        <b-row class="mt-2 text-center justify-content-center">
          <h1 class="cost-label">
            {{ selectedProduct.cost | formatCurrency }}<span class="duration-label ml-1">/{{ durationLabel }}</span>
            <b-badge
              v-if="annualPaidProduct && selectedProduct.productId === annualPaidProduct.productId"
              variant="primary"
              class="savings-badge ml-2 align-middle"
              data-test-hook="subscription-savings-badge"
              >{{ $t('xSavePercent') }}</b-badge
            >
          </h1>
        </b-row>
        <b-row class="mt-2">
          <strong>{{ selectedProduct.description }}</strong>
          <p class="mt-3">
            <strong data-test-hook="new-tank-subscription-modal-included-features-text">{{
              $t('xFeaturesIncluded')
            }}</strong>
          </p>
        </b-row>
        <b-row class="feature-list">
          <div class="d-flex align-items-center">
            <i
              class="fa-regular fa-gas-pump fa-fw pr-4"
              data-test-hook="new-tank-subscription-modal-fuel-monitor-feature-icon-1"
            />
            <div>
              <p data-test-hook="new-tank-subscription-modal-fuel-monitor-feature-title-1">
                <strong>{{ $t('xCurrentFuelLevel') }}</strong>
              </p>
              <p
                class="supporting-text"
                data-test-hook="new-tank-subscription-modal-fuel-monitor-feature-description-1"
              >
                {{ $t('xFuelLevelDescription') }}
              </p>
            </div>
          </div>
          <div class="d-flex align-items-center">
            <i
              class="fa-regular fa-chart-line fa-fw pr-4"
              data-test-hook="new-tank-subscription-modal-fuel-monitor-feature-icon-2"
            />
            <div>
              <p data-test-hook="new-tank-subscription-modal-fuel-monitor-feature-title-2">
                <strong> {{ $t('xFuelUsageHistory') }}</strong>
              </p>
              <p
                class="supporting-text"
                data-test-hook="new-tank-subscription-modal-fuel-monitor-feature-description-2"
              >
                {{ $t('xFuelUsageDescription') }}
              </p>
            </div>
          </div>
          <div class="d-flex align-items-center">
            <i
              class="fa-regular fa-warning fa-fw pr-4"
              data-test-hook="new-tank-subscription-modal-fuel-monitor-feature-icon-3"
            />
            <div>
              <p data-test-hook="new-tank-subscription-modal-fuel-monitor-feature-title-3">
                <strong> {{ $t('xFuelLevelWarnings') }}</strong>
              </p>
              <p
                class="supporting-text"
                data-test-hook="new-tank-subscription-modal-fuel-monitor-feature-description-3"
              >
                {{ $t('xFuelLevelWarningsDescription') }}
              </p>
            </div>
          </div>
          <div class="d-flex align-items-center">
            <i
              class="fa-regular fa-battery-full fa-fw pr-4"
              data-test-hook="new-tank-subscription-modal-fuel-monitor-feature-icon-4"
            />
            <div>
              <p data-test-hook="new-tank-subscription-modal-fuel-monitor-feature-title-4">
                <strong> {{ $t('xBatteryLevel') }}</strong>
              </p>
              <p
                class="supporting-text"
                data-test-hook="new-tank-subscription-modal-fuel-monitor-feature-description-4"
              >
                {{ $t('xBatteryLevelDescription') }}
              </p>
            </div>
          </div>
        </b-row>
      </div>
    </b-card>
    <b-row no-gutters class="mt-3">
      <b-col cols="12" class="float-left pl-2">
        <b-button
          variant="warning"
          class="w-100"
          :disabled="isCreatingPurchaseIntent"
          @click="$emit('openPayment')"
          data-test-hook="new-tank-subscription-modal-confirm-plan-button"
        >
          {{ $t('xContinue') }}
          <b-spinner class="ml-2" v-if="isCreatingPurchaseIntent" />
        </b-button>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import CadenceSelectionTabs from './Common/CadenceSelectionTabs.vue'

export default {
  props: ['selectedProduct', 'annualPaidProduct', 'monthlyPaidProduct', 'isCreatingPurchaseIntent'],
  emits: ['openPayment', 'setSelectedProduct'],
  components: {
    CadenceSelectionTabs
  },
  computed: {
    durationLabel() {
      if (this.selectedProduct?.productId === this.monthlyPaidProduct?.productId) {
        return this.$t('xMonth').toLowerCase()
      }

      return this.$t('xYear').toLowerCase()
    }
  },
  methods: {
    setSelectedProduct(product) {
      this.$emit('setSelectedProduct', product)
    }
  }
}
</script>
<style lang="scss">
#new-tank-subscription-view {
  h1 {
    font-weight: normal;
    line-height: 1.15;
  }

  .plan-details-header {
    font-size: 16px;
    color: var(--carbon-900);
    text-align: center;
  }

  .plan-header {
    text-align: center;
    padding: 5px 0px;
    font-size: 24px;
    line-height: 1.35;
    font-weight: 500;
  }

  .card-header {
    align-items: stretch;
    padding: 0;
    border-bottom: 0;
    height: 40px;
  }

  .sub-radio-dot {
    font-size: 0.875rem !important;
  }

  .paid-subs-info-card {
    border: 2px solid var(--carbon-300) !important;
    padding: 0;
    border-radius: 8px;

    .plan-header {
      background-color: var(--carbon-300);
    }
  }

  .feature-list {
    strong {
      font-size: 14px;
    }
  }

  .card-title {
    margin-bottom: 0;
  }

  .cost-label {
    color: var(--carbon-900);
    font-size: 32px;
    font-weight: bold;
    margin-bottom: 0;

    .duration-label {
      font-size: 16px !important;
      text-transform: lowercase !important;
      font-weight: normal;
    }

    .savings-badge {
      font-weight: normal;
      font-size: 14px;
      padding: 5px 7px;
    }
  }

  .subs-info-card-body {
    padding: 15px 30px !important;
    padding-top: 0 !important;
  }
}
</style>
